import { Box, Typography, Stack } from '@mui/material';
import { rollUpData } from 'src/utils/rollup';
import { currencyFormatter } from 'src/utils/currency-formatter';
import { categoryMap } from 'src/utils/catmap';
import brandColors from 'src/utils/brand-colors';
import {
  mergeDataSets,
  budgetSnapshotsByCategories,
  renderCell,
  splice,
  forecastCategories,
  calculateFullRangeData,
  nameForSegment,
  isRetirementAccount,
  accountPaysDividends,
  forecastSegments,
} from './utils';
import PrepareForecast from './prepareForecast';

export default function useIncomeForecast(params) {
  const { period } = params;
  const { aggregateData, ...forecastData } = runForecast(params);
  const { segments, budgets } = forecastData;

  const rows = getIncomeRows(aggregateData);

  const columns = segments
    .map((segment, index) => ({
      ...segment,
      field: segment.label,
      headerName: nameForSegment(segment, budgets, period),
      type: 'number',
      headerClassName: index % 2 === 0 ? 'even' : 'odd',
      valueGetter: (_, row) => row?.data?.[segment.label] ?? 0,
      valueFormatter: (value) => currencyFormatter.format(value),
      renderCell: (props) => renderCell({ period, budgets, segment, params: props }),
    }))
    .concat([
      {
        field: 'primaryCategory',
        headerName: 'Category',
        width: 150,
      },
      {
        field: 'detailedCategory',
        headerName: 'Detailed Category',
        width: 200,
        // valueFormatter: ({ value }) => categoryMap(value)?.detailed,
        renderCell: (p) => (
          <Stack direction="row" alignItems="center" padding={0.5}>
            <Box
              sx={{
                backgroundColor: p.row.color,
                width: '25px',
                height: '25px',
                borderRadius: '100%',
                mr: 1,
              }}
            />
            <Typography>{categoryMap(p.row.detailedCategory)?.detailed}</Typography>
          </Stack>
        ),
      },
    ]);

  const mergedData = splice(
    forecastData?.datasets?.historic,
    forecastData?.datasets?.forecast,
    segments,
    0,
    false // false to show the forecasted value for the current segment
  );

  const fullRangeIncome = calculateFullRangeData(segments, mergedData, 'income', false, params);
  const postTaxFullIncome = calculateFullRangeData(segments, mergedData, 'income', true, params); // post tax

  return {
    ...forecastData,
    aggregateData,
    fullRangeIncome,
    fullRangeIncomePostTax: postTaxFullIncome,
    segments,
    columns,
    rows,
    colorForKey: (key) => rows.find((e) => e.id === key)?.color,
  };
}

/**
 * Calculates and returns an expenses forecast based on provided data and parameters.
 *
 * @param {String} period - The period for which the forecast is to be made (e.g., 'monthly', 'yearly').
 * @param {Object} dateRange - The start and end dates defining the range of the forecast.
 * @param {Object} incomeData - The data related to income, including transactions and other relevant details.
 * @param {Array} budgetPatches - Modifications or adjustments to be applied to the budget.
 *
 * @return {Object} An object containing various details of the expenses forecast,
 *                  including budgets, data segments, aggregate data, historical series,
 *                  and forecasted series.
 */

function runForecast(params) {
  const { period, budgetSnapshots, averageIncomeGrowthRate, taxRateRetention } = params;

  const {
    txByCategories: _txByCategories,
    budgets,
    segments,
    calcSegments,
    calcPeriod,
  } = PrepareForecast({ ...params, invertValue: true });

  // convenience method to add/prepare a category if it doesn't exist
  const prepareDataSetCategory = (key, ds) => (ds?.[key] ? ds?.[key] : { [key]: { data: {} } });

  // Filter exclusively income categories
  const txByCategories = Object.fromEntries(
    Object.entries({
      ...prepareDataSetCategory('SOCIAL_SECURITY', _txByCategories),
      ...prepareDataSetCategory('INCOME_DIVIDENDS', _txByCategories),
      ...prepareDataSetCategory('INCOME_RETIREMENT', _txByCategories),
      ..._txByCategories,
    }).filter(([detailedCategory]) => categoryMap(detailedCategory, 'key')?.primary === 'Income')
  );

  const forecast = forecastCategories(
    txByCategories,
    calcSegments,
    budgets,
    calcPeriod,
    averageIncomeGrowthRate,
    params
  );

  const brokerageAccounts = params?.assets?.rows?.filter(accountPaysDividends);
  const retirementAccounts = brokerageAccounts?.filter(isRetirementAccount);
  const nonRetirementAccounts = brokerageAccounts?.filter((a) => !isRetirementAccount(a));

  // calculate income based on the account type
  const calculateIncome = (account, value, applyTaxationIfNeeded) => {
    let taxation = 1;

    if (isRetirementAccount(account) && applyTaxationIfNeeded) {
      taxation = taxRateRetention ?? 1;
    }

    return value * taxation;
  };

  // calculate income from accounts based on dividends
  const calculateIncomeForAccounts = (
    key,
    future,
    accounts,
    baseIncome = 0,
    applyTaxationIfNeeded
  ) => {
    // if we are not forecasting, return the original value
    if (!future) return baseIncome;

    // calculate income based on the account type
    return accounts.reduce((total, account) => {
      const value = account?.dividendPayments?.[key] ?? 0;
      return total + calculateIncome(account, value, applyTaxationIfNeeded);
    }, baseIncome);
  };

  const addExtraDatasets = (dataset) => ({
    ...dataset,
    // Add dividends income
    INCOME_DIVIDENDS: {
      // pre tax
      data: forecastSegments(segments, (key, { future }) =>
        calculateIncomeForAccounts(
          key,
          future,
          nonRetirementAccounts,
          dataset?.INCOME_DIVIDENDS?.data?.[key] || 0,
          false // skip taxation
        )
      ),
    },
    // Add retirement income
    INCOME_RETIREMENT: {
      data: forecastSegments(segments, (key, { future }) =>
        calculateIncomeForAccounts(
          key,
          future,
          retirementAccounts,
          dataset?.INCOME_RETIREMENT?.data?.[key] || 0,
          false // skip taxation
        )
      ),
    },
  });

  const options = { invert: true, filter: (cat) => cat === 'INCOME', patches: budgets };
  const budgetedSeries = budgetSnapshotsByCategories(period, budgetSnapshots, segments, options);

  // TODO: I think we can get rid of these two variables and move splicing further down the chain
  const mergedData = addExtraDatasets(mergeDataSets(txByCategories, forecast, calcSegments, 0));
  const aggregateData = rollUpData(calcSegments, segments, mergedData, period);

  const forecastedSeries = addExtraDatasets(rollUpData(calcSegments, segments, forecast, period));
  const historicalSeries = rollUpData(calcSegments, segments, txByCategories, period);

  const datasets = {
    historic: historicalSeries,
    budgeted: budgetedSeries,
    forecast: forecastedSeries,
    splice,
  };

  return {
    budgets,
    segments,
    aggregateData,
    budgetedSeries,
    historicalSeries,
    forecastedSeries,
    datasets,
  };
}

function getIncomeRows(aggregateData) {
  return Object.entries(aggregateData)
    .filter(([detailedCategory]) => categoryMap(detailedCategory, 'key')?.primary === 'Income')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([detailedCategory, { data, postTax }], index) => ({
      id: detailedCategory,
      data,
      ...(postTax && { postTax }), // TODO: I don't think this gets used anywhere
      primaryCategory: categoryMap(detailedCategory, 'key')?.primary,
      detailedCategory,
      color: brandColors[index % brandColors.length],
    }));
}
